import { ReactNode, useEffect, useEffectEvent, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCurrentUser,
  useSendMagicLink,
  useVerifyMagicLink,
  useVerifyOTP,
  useLogout,
  useRefreshToken,
} from '@/hooks/react-query/useAuth';
import { User } from '@/types/auth';
import { userHasPermission } from '@/utils/permissions';
import { AuthContext, AuthContextType } from './auth-context-definition';
import { API_URL } from '@/config/api';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();

  // React Query hooks
  const {
    data: user,
    isLoading: loading,
    error,
    refetch: refetchUser,
  } = useCurrentUser();
  const sendMagicLinkMutation = useSendMagicLink();
  const verifyMagicLinkMutation = useVerifyMagicLink();
  const verifyOTPMutation = useVerifyOTP();
  const logoutMutation = useLogout();
  const refreshTokenMutation = useRefreshToken();

  const clearAuth = (): void => {
    localStorage.removeItem('access_token');
    // Clear all React Query caches on logout (but don't invalidate - just clear to prevent refetching)
    queryClient.clear();
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const response = await refreshTokenMutation.mutateAsync();

      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token);
        // Refetch user data after successful refresh
        await refetchUser();
        return true;
      } else {
        // If refresh fails, clear auth
        clearAuth();
        return false;
      }
    } catch {
      clearAuth();
      return false;
    }
  };

  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);

  // Handle authentication errors - useEffectEvent ensures we always have latest clearAuth/refreshToken
  const handleUnauthorized = useEffectEvent(async (event: Event) => {
    // No token means the session is already cleared
    if (!localStorage.getItem('access_token')) return;

    // Check if we should skip refresh (e.g., refresh endpoint itself returned 401)
    const customEvent = event as CustomEvent<{ skipRefresh?: boolean }>;
    const skipRefresh = customEvent.detail?.skipRefresh || false;

    if (skipRefresh) {
      clearAuth();
      return;
    }

    // Reuse an in-flight refresh so N concurrent 401s trigger one refresh, not N.
    if (!refreshInFlightRef.current) {
      refreshInFlightRef.current = refreshToken().finally(() => {
        refreshInFlightRef.current = null;
      });
    }
    const refreshed = await refreshInFlightRef.current;
    if (!refreshed) clearAuth();
  });

  useEffect(() => {
    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const login = async (
    email: string,
    next?: string
  ): Promise<{ success: boolean; message: string; magic_link?: string }> => {
    try {
      const response = await sendMagicLinkMutation.mutateAsync({ email, next });

      return {
        success: response.success,
        message: response.message,
        magic_link: response.magic_link,
      };
    } catch {
      return {
        success: false,
        message: 'Failed to send login link. Please try again.',
      };
    }
  };

  const verifyMagicLink = async (
    token: string
  ): Promise<{ success: boolean; user?: User; message?: string }> => {
    try {
      const response = await verifyMagicLinkMutation.mutateAsync(token);

      if (response.success && response.user) {
        // The mutation hook will handle cache invalidation and refetch user data
        return {
          success: true,
          user: response.user,
        };
      } else {
        return {
          success: false,
          message: response.message || 'Invalid or expired login link',
        };
      }
    } catch {
      return {
        success: false,
        message: 'Failed to verify login link. Please try again.',
      };
    }
  };

  const verifyOTP = async (
    email: string,
    code: string
  ): Promise<{ success: boolean; user?: User; message?: string }> => {
    try {
      const response = await verifyOTPMutation.mutateAsync({ email, code });

      if (response.success && response.user) {
        return {
          success: true,
          user: response.user,
        };
      } else {
        return {
          success: false,
          message: response.message || 'Invalid code',
        };
      }
    } catch {
      return {
        success: false,
        message: 'Failed to verify code. Please try again.',
      };
    }
  };

  const loginWithGoogle = async (next?: string): Promise<void> => {
    // Redirect to backend Google OAuth login endpoint. A `next` return path is
    // forwarded so the callback can resume the flow after Google returns.
    const query = next ? `?next=${encodeURIComponent(next)}` : '';
    window.location.href = `${API_URL}/auth/google/login${query}`;
  };

  const logout = async (): Promise<void> => {
    // Clear local state first to prevent any subsequent API calls from failing
    clearAuth();

    // Then attempt to notify the server about logout (but don't worry if it fails)
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      // Silently ignore logout API errors since we've already cleared local state
      console.debug(
        'Logout API call failed (this is expected if the session was already expired):',
        error
      );
    }
  };

  const isAuthenticated = !!user;

  const hasPermission = (permissionName: string): boolean =>
    userHasPermission(user, permissionName);

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    verifyMagicLink,
    verifyOTP,
    logout,
    refreshToken,
    clearAuth,
    isAuthenticated,
    hasPermission,
  };

  return <AuthContext value={value}>{children}</AuthContext>;
};
